import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Download, ExternalLink, FolderOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { PlatformIcon } from '@/components/PlatformIcon';
import { parseCsv } from '@/lib/bulk-import/csv';
import { ApiError, type RepeatSchedule } from '../api/types';
import { useDiscoveryClient } from '../api/context';
import { useScheduleAdded } from '../hooks/schedules';
import { profileUrl } from '../lib/platform';
import { OutreachStatusBadge } from '../components/OutreachStatus';
import { formatCount } from '../components/lead-table/rows';
import { relativeTime } from '../search/controls';
import { useDiscoveryPaths } from '../workspace/paths';
import { Note } from '../workspace/ui';
import { fileSlug } from './model';

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Everyone a schedule has added, across all its runs — opened in place under
 * its Repeat runs row, with export and copy for the whole list (not just the
 * page on screen).
 */
export function ScheduleCreators({ schedule }: { schedule: RepeatSchedule }): JSX.Element {
  const client = useDiscoveryClient();
  const paths = useDiscoveryPaths();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useScheduleAdded(schedule.publicId, page);
  const [busy, setBusy] = useState<'export' | 'copy' | null>(null);

  async function exportCsv() {
    setBusy('export');
    try {
      saveBlob(await client.schedules.exportAdded(schedule.publicId), `${fileSlug(schedule.name)}-creators.csv`);
    } catch (e) {
      toast.error("Couldn't export the creators", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function copyHandles() {
    setBusy('copy');
    try {
      const rows = parseCsv(await (await client.schedules.exportAdded(schedule.publicId)).text(), ',');
      const col = rows[0]?.indexOf('username') ?? -1;
      const handles = col < 0 ? [] : rows.slice(1).map((r) => r[col]).filter(Boolean).map((h) => `@${h}`);
      await navigator.clipboard.writeText(handles.join('\n'));
      toast.success(`Copied ${handles.length.toLocaleString()} handle${handles.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error("Couldn't copy the handles", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  const has = (data?.total ?? 0) > 0;

  return (
    <div className="space-y-3 py-3" data-schedule-creators={schedule.publicId}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">
          {data ? `${data.total.toLocaleString()} creator${data.total === 1 ? '' : 's'} added` : 'Creators added'}
          <span className="ml-1.5 font-normal text-muted-foreground">across every run, newest first</span>
        </h3>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button type="button" variant="outline" size="sm" disabled={!has || busy !== null} onClick={exportCsv}>
            {busy === 'export' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export CSV
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!has || busy !== null} onClick={copyHandles}>
            {busy === 'copy' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            Copy handles
          </Button>
          {schedule.folder && (
            <Button asChild variant="ghost" size="sm">
              <Link to={paths.folder(schedule.folder.publicId, 'leads')}>
                <FolderOpen className="h-3.5 w-3.5" />
                Open folder
              </Link>
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Note tone="error" role="alert">
          {error instanceof ApiError && error.status === 404
            ? "This list needs a Discovery service update that hasn't finished deploying. Run history still shows each run's creators."
            : `Couldn't load the creators: ${(error as Error).message}`}
        </Note>
      )}
      {isLoading && <p className="text-sm text-muted-foreground">Loading creators…</p>}
      {data && data.total === 0 && (
        <p className="text-sm text-muted-foreground">
          No creators from this schedule are in the folder. Either no run has added anyone yet, or they were removed
          since.
        </p>
      )}

      {data && data.creators.length > 0 && (
        <>
          <table className="w-full table-fixed text-sm" aria-label={`Creators added by ${schedule.name}`}>
            <thead>
              <tr className="text-left text-xs font-semibold text-muted-foreground/80">
                <th className="py-1.5 pr-3 font-semibold">Creator</th>
                <th className="w-24 py-1.5 pr-3 text-right font-semibold">Followers</th>
                <th className="hidden w-24 py-1.5 pr-3 text-right font-semibold lg:table-cell">Avg views</th>
                <th className="hidden w-36 py-1.5 pr-3 font-semibold xl:table-cell">Country</th>
                <th className="hidden w-24 py-1.5 pr-3 font-semibold md:table-cell">Added</th>
                <th className="w-40 py-1.5 font-semibold">Status now</th>
              </tr>
            </thead>
            <tbody>
              {data.creators.map((c) => (
                <tr key={`${c.platform}:${c.username}`} className="border-t border-border/60">
                  <td className="py-2 pr-3">
                    <a
                      href={profileUrl(c.platform, c.username)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex max-w-full items-center gap-1.5 font-medium hover:underline"
                    >
                      <PlatformIcon platform={c.platform} size={14} className="shrink-0 text-muted-foreground" />
                      <span className="truncate">@{c.username}</span>
                      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                    </a>
                    {c.nickname && c.nickname !== c.username && (
                      <p className="truncate pl-5 text-xs text-muted-foreground">{c.nickname}</p>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">{formatCount(c.followers)}</td>
                  <td className="hidden whitespace-nowrap py-2 pr-3 text-right tabular-nums lg:table-cell">
                    {formatCount(c.avgViews)}
                  </td>
                  <td className="hidden truncate py-2 pr-3 xl:table-cell">
                    {c.country ? c.country.name : <span className="text-muted-foreground">Unknown</span>}
                  </td>
                  <td className="hidden whitespace-nowrap py-2 pr-3 text-muted-foreground md:table-cell">
                    {relativeTime(c.runAt)}
                  </td>
                  <td className="py-2">
                    <OutreachStatusBadge creator={c} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={data.page} limit={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
